// HASC Phase 2, "Register with HASC" (spec locked by George 2026-07-13,
// extended 2026-09-23: crew-wide registration, the already-valid guard, the
// location and date rules). The client half of server/routes/hasc.js.
//
// REAL MONEY. A confirmed click here creates a BILLABLE HASC registration and
// there is no sandbox. Nothing in this file decides money or eligibility: the
// server says who may register (status.can_register, the pilot rule), which
// locations offer a class, whether the class is already valid, who is
// blocked, and it re-checks all of it before it books. Every confirm is an
// in-page panel, never window.confirm / alert / prompt.
//
//   useHascStatus          GET /registration/status (null = show nothing)
//   HascRegisterButton     the per-check button in the Site Ready modal
//   HascRegisterPanel      the single-registration confirm panel
//   HascCrewRegister       "Register missing classes (N)" + the crew review table
//   HascRegistrationList   registrations with Refresh / Cancel (in-page confirm)
//   HascRegistrationAdmin  Admin -> Backup "HASC Training Sync" card section
import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { styles, colors, fmtDate, fmtDateTime } from './UI';
import { hascCodes, addDaysIso, chunkByEmployee, mayBeBooked } from '../utils/hascView';

// Display only (which option to preselect, which to grey out): the server
// applies the same LinkOnline rule again before it books anything.
const isLinkOnline = (loc) => /^link\s*online$/i.test(String(loc == null ? '' : loc).trim());
const linkOnlineIn = (list) => (list || []).find(isLinkOnline) || '';
// LinkOnline is preselected only when HASC's own course list says it offers
// the class (linkonline_offered true; null = every course list failed, so it
// is unverified) and the person has the email LinkOnline needs.
const linkOnlineDefault = (course, hasEmail) =>
  (course && hasEmail && course.linkonline_offered === true ? linkOnlineIn(course.locations) : '');

const REQUESTED_NOTE = 'requested; HASC books the next open date if this one is full';
const LINKONLINE_RULE = 'LinkOnline: start the course within 5 days of registration and finish within 5 days of starting, or HASC marks it No Show.';
const PFI_TITLE = "HASC's post-fail instructions: Stop, Retry or Continue. Stop is the default.";

const fieldLabel = { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: colors.textMuted, fontWeight: 700, marginBottom: 3 };
const btnSm = { ...styles.btnSecondary, fontSize: 11, padding: '3px 10px', minWidth: 0 };
const inputSm = { ...styles.input, fontSize: 12, padding: '5px 8px' };
const amberBox = { background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A', borderRadius: 6, padding: '6px 10px', fontSize: 12 };
const okBox = { background: '#ECFDF5', color: '#065F46', border: '1px solid #A7F3D0', borderRadius: 6, padding: '8px 10px', fontSize: 12 };
const errBox = { ...styles.errorBox, fontSize: 12, marginBottom: 8 };
const moneyLine = { fontSize: 12, fontWeight: 700, color: '#92400E' };

// Who sees the Register buttons: a configured HASC account AND a user the
// server allows (can_register is false for a rep, for ops / supervisor while
// the pilot is on, and for an impersonated session).
export const hascVisible = (status) => !!(status && status.configured && status.can_register);
export const hascPilotLabel = (status) => (status && status.pilot ? ' (pilot: admin & leadership only)' : '');

// A Site Ready check the button belongs on: a failing or expiring class that
// carries a course code HASC can take (the server's codeAlternates rule, so
// "H2S (Clear)" gets no button and no 400) on the Training or Safety Council
// card, and that ISNetworld does not govern (only ISN can clear those).
// withJob false: the report is not for a job (Site Access with no job picked
// judges against its Ready-through date, but the server judges a no-job
// registration against today + 30), so an amber Expiring class gets no
// button there: the server could only answer "already valid".
export const isHascCheck = (c, withJob = true) => !!(c && (c.status === 'fail' || (c.status === 'warn' && withJob))
  && c.category === 'training' && hascCodes(c.course_code).length > 0 && !c.isn_governed);

export function useHascStatus(active = true) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (!active) { setStatus(null); return undefined; }
    let alive = true;
    api.hascRegistrationStatus()
      .then(s => { if (alive) setStatus(s); })
      .catch(() => { if (alive) setStatus(null); }); // a rep's 403 or a failed read: no button
    return () => { alive = false; };
  }, [active]);
  return status;
}

const STATUS_TAG = {
  Pending: ['#DBEAFE', '#1E40AF'],
  'In Progress': ['#E0E7FF', '#3730A3'],
  Complete: ['#D1FAE5', '#065F46'],
  'No Show': ['#FEE2E2', '#991B1B'],
  Cancelled: ['#F3F4F6', '#6B7280'],
  'Not Found': ['#F3F4F6', '#6B7280'],
  Submitting: ['#F3F4F6', '#374151'],
  Unconfirmed: ['#FEF3C7', '#92400E'],
};
function StatusTag({ row }) {
  const [bg, fg] = row.status === 'Complete' && row.pass === false ? ['#FEE2E2', '#991B1B'] : (STATUS_TAG[row.status] || ['#F3F4F6', '#374151']);
  const text = row.status === 'Complete' && row.pass != null ? `Complete · ${row.pass ? 'passed' : 'failed'}` : row.status;
  return <span style={{ background: bg, color: fg, borderRadius: 8, padding: '1px 8px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{text}</span>;
}

// Registrations as the server returns them (publicRow). Refresh = poll HASC
// now ($0). Cancel only where the server says can_cancel (HASC cancels only a
// Pending one; a row HASC never confirmed closes in the CRM only), always
// behind an in-page confirm.
export function HascRegistrationList({ rows, canWrite, onChanged, showEmployee = true, showJob = true }) {
  const [busyId, setBusyId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [msgs, setMsgs] = useState({}); // id -> { ok, text }
  const note = (id, ok, text) => setMsgs(m => ({ ...m, [id]: { ok, text } }));

  const refresh = async (r) => {
    setBusyId(r.id);
    try {
      const d = await api.hascRefreshRegistration(r.id);
      const reg = d.registration || {};
      note(r.id, true, `Checked with HASC: ${reg.status || 'no status'}${reg.message ? ` (${reg.message})` : ''}.`);
      if (onChanged) onChanged(reg);
    } catch (e) { note(r.id, false, e.message); }
    finally { setBusyId(null); }
  };
  const cancel = async (r) => {
    setBusyId(r.id); setConfirmId(null);
    try {
      const d = await api.hascCancelRegistration(r.id);
      note(r.id, true, d.crm_only
        ? 'Closed in the CRM only: HASC never confirmed it. Check HASC LINK that nothing was booked.'
        : `Cancelled at HASC${d.registration && d.registration.message ? ` (${d.registration.message})` : ''}.`);
      if (onChanged) onChanged(d.registration || null);
    } catch (e) { note(r.id, false, e.message); }
    finally { setBusyId(null); }
  };

  if (!rows || !rows.length) return <div style={{ fontSize: 12, color: colors.textMuted }}>No HASC registrations yet.</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ ...styles.table, fontSize: 12 }}>
        <thead><tr>
          <th style={styles.th}>Registered</th>
          {showEmployee && <th style={styles.th}>Employee</th>}
          <th style={styles.th}>Class</th>
          <th style={styles.th}>Location</th>
          <th style={styles.th} title={`The date asked for: ${REQUESTED_NOTE}. HASC does not report the booked date.`}>Requested date</th>
          {showJob && <th style={styles.th}>Job / PO</th>}
          <th style={styles.th}>Status</th>
          <th style={styles.th}>HASC #</th>
          <th style={styles.th}></th>
        </tr></thead>
        <tbody>
          {rows.map(r => {
            const crmOnly = r.registration_id == null;
            const m = msgs[r.id];
            return (
              <tr key={r.id}>
                <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                  {fmtDateTime(r.created_at)}
                  {r.requested_by_name && <div style={{ color: colors.textMuted, fontSize: 11 }}>by {r.requested_by_name}</div>}
                </td>
                {showEmployee && <td style={styles.td}>{r.employee_name || '—'}</td>}
                <td style={styles.td}>
                  <strong>{r.course_code}</strong>{r.course_name ? <div style={{ color: colors.textMuted, fontSize: 11 }}>{r.course_name}</div> : null}
                </td>
                <td style={styles.td}>{r.location}{r.account && r.account !== 'HASC' ? <div style={{ color: colors.textMuted, fontSize: 11 }}>{r.account} account</div> : null}</td>
                <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                  {fmtDate(r.desired_date) || '—'}
                  <div style={{ color: colors.textMuted, fontSize: 10 }}>requested</div>
                </td>
                {showJob && (
                  <td style={styles.td}>
                    {r.job_number || r.site || '—'}
                    {r.po_number ? <div style={{ color: colors.textMuted, fontSize: 11 }}>PO {r.po_number}</div> : null}
                  </td>
                )}
                <td style={{ ...styles.td, maxWidth: 260 }}>
                  <StatusTag row={r} />
                  {r.message && <div style={{ fontSize: 11, color: r.status === 'Unconfirmed' ? '#92400E' : '#374151', marginTop: 2 }}>{r.message}</div>}
                  {r.last_polled_at && <div style={{ fontSize: 10, color: colors.textMuted, marginTop: 2 }}>checked {fmtDateTime(r.last_polled_at)}</div>}
                </td>
                <td style={{ ...styles.td, fontFamily: 'monospace' }}>{r.registration_id || '—'}</td>
                <td style={{ ...styles.td, minWidth: 150 }}>
                  {confirmId === r.id ? (
                    <div style={{ fontSize: 11 }}>
                      <div style={{ marginBottom: 4, color: '#92400E', fontWeight: 600 }}>
                        {crmOnly
                          ? 'HASC never confirmed this one. Checked HASC LINK that nothing was booked? Close it in the CRM?'
                          : `Cancel HASC registration #${r.registration_id}?`}
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button type="button" style={{ ...btnSm, color: colors.red, borderColor: colors.red }} onClick={() => cancel(r)}>
                          {crmOnly ? 'Yes, close it' : 'Yes, cancel'}
                        </button>
                        <button type="button" style={btnSm} onClick={() => setConfirmId(null)}>Keep</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {!crmOnly && (
                        <button type="button" style={btnSm} disabled={busyId === r.id} onClick={() => refresh(r)}
                          title="Ask HASC for this registration's status now ($0).">
                          {busyId === r.id ? '…' : 'Refresh'}
                        </button>
                      )}
                      {canWrite && r.can_cancel && (
                        <button type="button" style={btnSm} disabled={busyId === r.id} onClick={() => setConfirmId(r.id)}
                          title={crmOnly ? 'HASC never confirmed this registration: close the CRM row after checking HASC LINK.' : 'HASC cancels a Pending registration only.'}>
                          {crmOnly ? 'Close in CRM' : 'Cancel'}
                        </button>
                      )}
                    </div>
                  )}
                  {m && <div style={{ fontSize: 11, marginTop: 4, color: m.ok ? '#065F46' : '#B91C1C' }}>{m.text}</div>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// The per-check button in the Site Ready modal.
export function HascRegisterButton({ status, onClick, disabled = false }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      style={{ ...btnSm, marginTop: 6, color: '#1E40AF', borderColor: '#93C5FD', background: '#EFF6FF' }}
      title="Register this person for this class with HASC. Opens a confirm panel first; nothing is booked until you confirm.">
      🎓 Register with HASC{hascPilotLabel(status)}
    </button>
  );
}

// One employee + one class (the check's course code, "/" alternates kept).
// Every choice below comes from GET /registration/options; Confirm posts once.
// onBusy(true|false) brackets the billable POST so the host can keep itself
// open until the result is on screen; onChanged fires when a Refresh or
// Cancel in the list below changed a registration's status.
export function HascRegisterPanel({ employeeId, employeeName, courseCode, checkLabel, projectId, onClose, onRegistered, onBusy, onChanged }) {
  const [opts, setOpts] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [account, setAccount] = useState('');
  const [code, setCode] = useState('');
  const [location, setLocation] = useState('');
  const [date, setDate] = useState('');
  const [po, setPo] = useState('');
  const [pfi, setPfi] = useState('Stop');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [submitErr, setSubmitErr] = useState('');
  const [submitMaybe, setSubmitMaybe] = useState(false); // the error left an Unconfirmed row: it MAY be booked
  const formSet = useRef(false);
  const loadedFor = useRef('');
  // Set synchronously on Confirm: `busy` only disables the button after a
  // re-render, so two clicks in the same instant would both POST.
  const inFlight = useRef(false);

  useEffect(() => {
    let alive = true;
    const key = `${employeeId}|${courseCode}|${projectId || ''}`;
    setLoading(true); setLoadErr('');
    api.hascRegistrationOptions(employeeId, courseCode, projectId)
      .then(o => { if (alive) { loadedFor.current = key; setOpts(o); } })
      .catch(e => {
        if (!alive) return;
        // A failed RELOAD (after Confirm, Refresh or Cancel) keeps what is on
        // screen: the HASC number, the "tell them yourself" warning or the
        // "may be booked" error must not vanish with it.
        if (loadedFor.current !== key) setOpts(null);
        setLoadErr(e.message);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [employeeId, courseCode, projectId, tick]);

  // The course + location a fresh pick of this account starts on: the
  // server's default course, LinkOnline when HASC's course list confirms it
  // offers it and the employee has an email (LinkOnline needs one), otherwise
  // nothing preselected.
  const pickFor = (o, acct) => {
    const off = (o.offerings || []).find(x => x.account === acct);
    const list = off ? (off.courses || []).filter(c => (c.locations || []).length) : [];
    const c = list.find(x => x.course_code === off.default_course_code) || list[0] || null;
    return { code: c ? c.course_code : '', location: linkOnlineDefault(c, o.employee_has_email) };
  };

  useEffect(() => {
    if (!opts || formSet.current) return;
    formSet.current = true;
    const acct = opts.default_account || (opts.accounts || [])[0] || '';
    const d = pickFor(opts, acct);
    setAccount(acct); setCode(d.code); setLocation(d.location);
    setDate(opts.default_desired_date || opts.today || '');
    setPo(opts.default_po || '');
    setPfi(opts.default_pfi || 'Stop');
  }, [opts]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeAccount = (acct) => {
    const d = pickFor(opts, acct);
    setAccount(acct); setCode(d.code); setLocation(d.location); setSubmitErr(''); setSubmitMaybe(false);
  };
  const offering = opts ? (opts.offerings || []).find(o => o.account === account) || null : null;
  const offered = offering ? (offering.courses || []).filter(c => (c.locations || []).length) : [];
  const course = offered.find(c => c.course_code === code) || null;
  const changeCourse = (c) => {
    const next = offered.find(x => x.course_code === c);
    setCode(c);
    setLocation(linkOnlineDefault(next, opts.employee_has_email));
  };
  const hasEmail = !!(opts && opts.employee_has_email);
  const codes = opts ? (opts.course_codes || []).join(' / ') : courseCode;
  const unverified = new Set(course ? course.unverified_locations || [] : []);
  // A class HASC already shows passed that the training feed has not brought
  // in: the server refuses it (409, after the already-valid and open checks)
  // and decides the same hold for the panel (options.passed_registration).
  const passed = opts ? opts.passed_registration || null : null;
  // The horizon is the job's only when it is later than the today + 30 floor.
  const jobHorizon = !!(opts && opts.project && opts.horizon && opts.horizon !== addDaysIso(opts.today, 30));

  // Reasons the SERVER reported that stop this registration (shown, never re-derived).
  const blockers = [];
  if (opts) {
    if (opts.employee && opts.employee.active === false) blockers.push(`${opts.employee.name} is deactivated: reactivate before registering.`);
    if (opts.employee && opts.employee.name_error) blockers.push(opts.employee.name_error);
    if (offering && !offering.ok) blockers.push(`Could not read ${offering.account}'s locations from HASC: ${offering.error}`);
    if (offering && offering.identifier_error) blockers.push(offering.identifier_error);
    if (offering && offering.ok && !offered.length) blockers.push(`No ${offering.account} location offers ${codes}.`);
    if (course && !hasEmail && course.locations.every(isLinkOnline)) {
      blockers.push(`No email on file, and only LinkOnline offers ${course.course_code}: LinkOnline needs an email. Add it on the roster.`);
    }
  }
  const missing = !location ? 'Pick a location.' : !date ? 'Pick a requested date.' : !po.trim() ? 'PO number required: it prints on HASC\'s bill.' : '';
  const lateStart = !!(opts && opts.first_day_onsite && date && date >= opts.first_day_onsite);

  const submit = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true); setSubmitErr(''); setSubmitMaybe(false); setResult(null);
    if (onBusy) onBusy(true);
    try {
      const r = await api.hascRegister({
        employee_id: employeeId, course_code: code, account, location, desired_date: date,
        po_number: po.trim(), pfi, project_id: projectId || undefined,
      });
      setResult(r);
      if (onRegistered) onRegistered(r);
    } catch (e) {
      setSubmitErr(e.message);
      setSubmitMaybe(!!(e.data && e.data.registration && e.data.registration.status === 'Unconfirmed'));
    } finally {
      inFlight.current = false;
      setBusy(false);
      if (onBusy) onBusy(false);
      setTick(t => t + 1); // the list below shows the new row, or an Unconfirmed one
    }
  };

  const email = result && result.email ? result.email : null;
  const emailText = email
    ? (email.sent ? `Emailed ${email.to}${email.cc ? ` (cc ${email.cc})` : ''}.` : (email.warning || email.error || 'Email not sent: tell them yourself.'))
    : '';
  const extraWarning = result && result.warning && (!email || (result.warning !== email.warning && result.warning !== email.error)) ? result.warning : '';
  const reg = result ? result.registration : null;

  return (
    <div style={{ marginTop: 6, marginBottom: 8, padding: '10px 12px', border: '1px solid #93C5FD', borderRadius: 8, background: '#F8FAFF' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: colors.charcoal }}>
          🎓 Register with HASC{hascPilotLabel(opts)}
          <div style={{ fontSize: 12, fontWeight: 400, color: colors.textMuted }}>{employeeName} · {checkLabel}</div>
        </div>
        <button type="button" style={btnSm} disabled={busy} onClick={onClose}
          title={busy ? 'Registering with HASC: wait for the result.' : undefined}>Close</button>
      </div>

      {loading && !opts && <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 8 }}>Checking HASC locations and courses ($0)…</div>}
      {loadErr && <div style={{ ...errBox, marginTop: 8 }}>{opts ? `Could not reload the HASC details: ${loadErr}` : loadErr}</div>}

      {opts && (
        <>
          <div style={{ fontSize: 12, color: '#374151', marginTop: 6 }}>
            Current record: <strong>{opts.current_record || 'none on file'}</strong>
            {opts.horizon ? <> · must hold through <strong>{fmtDate(opts.horizon)}</strong>{jobHorizon ? ' (later of last day / job end, + 30)' : ' (today + 30)'}</> : null}
          </div>

          {result && (
            <div style={{ ...okBox, marginTop: 8 }}>
              <div style={{ fontWeight: 700 }}>
                ✓ Registered with HASC{reg && reg.registration_id ? `: registration #${reg.registration_id}` : ''}
              </div>
              {reg && (
                <div>{reg.course_code}{reg.course_name ? ` (${reg.course_name})` : ''} at {reg.location}, {fmtDate(reg.desired_date)} ({REQUESTED_NOTE}).</div>
              )}
              {result.hasc_name && <div>HASC knows this ID as {result.hasc_name}.</div>}
              {emailText && <div style={{ color: email && email.sent ? '#065F46' : '#92400E', fontWeight: email && email.sent ? 400 : 600 }}>{emailText}</div>}
              {extraWarning && <div style={{ color: '#92400E', fontWeight: 600 }}>{extraWarning}</div>}
            </div>
          )}
          {submitErr && (submitMaybe ? (
            <div style={{ ...amberBox, marginTop: 8, fontWeight: 600 }}>⚠ Not a refusal: it may be booked. {submitErr}</div>
          ) : (
            <div style={{ ...errBox, marginTop: 8 }}>{submitErr}</div>
          ))}

          {!result && (opts.already_valid ? (
            <div style={{ ...okBox, marginTop: 8 }}>
              {opts.already_valid.until
                ? <>Already valid until <strong>{fmtDate(opts.already_valid.until)}</strong> ({opts.already_valid.cert}). Nothing to register.</>
                : <>Already valid: {opts.already_valid.cert} has no expiry. Nothing to register.</>}
            </div>
          ) : opts.open_registration ? (
            <div style={{ ...amberBox, marginTop: 8 }}>
              Already has an open HASC registration for {opts.open_registration.course_code} ({opts.open_registration.status}
              {opts.open_registration.desired_date ? `, requested ${fmtDate(opts.open_registration.desired_date)}` : ''}).
              {opts.open_registration.status === 'Unconfirmed' && opts.open_registration.registration_id
                ? ' HASC answered with an error but returned that registration number: Refresh it below to see what HASC has.'
                : ' Refresh or cancel it below before registering again.'}
            </div>
          ) : passed ? (
            <div style={{ ...okBox, marginTop: 8 }}>
              HASC shows {passed.course_code} {passed.pass === true ? 'passed' : 'Complete (pass not reported)'}
              {' '}(registration {passed.registration_id ? `#${passed.registration_id}` : `CRM row ${passed.id}`}
              {passed.updated_at ? `, as of ${fmtDate(passed.updated_at)}` : ''}).
              {' '}Nothing to register: the nightly HASC training sync brings the pass onto the training record, and the
              Site Ready check follows. If it never shows, check the roster for a duplicate of {(opts.employee && opts.employee.name) || employeeName}.
            </div>
          ) : !opts.can_register ? (
            <div style={{ ...amberBox, marginTop: 8 }}>HASC registration is not switched on for you yet.</div>
          ) : (
            <div style={{ marginTop: 8 }}>
              {blockers.map((b, i) => <div key={i} style={{ ...errBox, marginBottom: 6 }}>{b}</div>)}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
                <div>
                  <div style={fieldLabel}>Employee</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{(opts.employee && opts.employee.name) || employeeName}</div>
                </div>
                <div>
                  <div style={fieldLabel}>Class</div>
                  {offered.length > 1 ? (
                    <select value={code} onChange={e => changeCourse(e.target.value)} style={inputSm}>
                      {offered.map(c => <option key={c.course_code} value={c.course_code}>{c.course_code}{c.course_name ? ` · ${c.course_name}` : ''}</option>)}
                    </select>
                  ) : (
                    <div style={{ fontSize: 13 }}><strong>{course ? course.course_code : codes}</strong>{course && course.course_name ? ` · ${course.course_name}` : ''}</div>
                  )}
                </div>
                {(opts.accounts || []).length >= 2 && (
                  <div>
                    <div style={fieldLabel}>Council account</div>
                    <select value={account} onChange={e => changeAccount(e.target.value)} style={inputSm}
                      title="A class registered under one account shows up only in that account's training feed.">
                      {opts.accounts.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                )}
                {course && (
                  <div>
                    <div style={fieldLabel}>Location</div>
                    <select value={location} onChange={e => { setLocation(e.target.value); setSubmitErr(''); }} style={inputSm}>
                      <option value="">Pick a location…</option>
                      {course.locations.map(l => {
                        const online = isLinkOnline(l);
                        return (
                          <option key={l} value={l} disabled={online && !hasEmail}>
                            {l}{online ? ' (online)' : ''}{online && !hasEmail ? ' (needs an email on file)' : ''}{course.lookup_failed || unverified.has(l) ? ' (unverified)' : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}
                <div>
                  <div style={fieldLabel}>Requested date</div>
                  <input type="date" value={date} min={opts.today} max="2100-12-31" onChange={e => setDate(e.target.value)} style={inputSm} />
                  <div style={{ fontSize: 10, color: colors.textMuted, marginTop: 2 }}>{REQUESTED_NOTE}</div>
                </div>
                <div>
                  <div style={fieldLabel}>PO number</div>
                  <input value={po} onChange={e => setPo(e.target.value)} maxLength={120} placeholder="PO (required)" style={inputSm}
                    title="Prints on HASC's bill. Defaults to the job number." />
                </div>
                <div>
                  <div style={fieldLabel}>Pfi</div>
                  <select value={pfi} onChange={e => setPfi(e.target.value)} style={inputSm} title={PFI_TITLE}>
                    {(opts.pfi_values || ['Stop', 'Retry', 'Continue']).map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              </div>

              {course && course.lookup_failed && (
                <div style={{ ...amberBox, marginTop: 8 }}>HASC's course lists did not load, so these locations are unverified. The server checks again with HASC before it books.</div>
              )}
              {course && !course.lookup_failed && unverified.size > 0 && (
                <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 6 }}>Could not check: {[...unverified].join(', ')} (their course lists did not load).</div>
              )}
              {opts.project && (
                <div style={{ fontSize: 12, color: '#374151', marginTop: 8 }}>
                  {opts.first_day_onsite
                    ? <>First day onsite: <strong>{fmtDate(opts.first_day_onsite)}</strong></>
                    : <span style={{ color: colors.textMuted }}>No first day onsite on file for this job.</span>}
                </div>
              )}
              {lateStart && (
                <div style={{ ...amberBox, marginTop: 4 }}>
                  ⚠ The requested date is on or after their first day onsite ({fmtDate(opts.first_day_onsite)}): the class would land after they start on the job.
                </div>
              )}
              <div style={{ fontSize: 12, marginTop: 6, color: hasEmail ? '#374151' : '#92400E', fontWeight: hasEmail ? 400 : 600 }}>
                {hasEmail
                  ? '✉ Email on file: the CRM emails them the class details and copies you.'
                  : '✉ No email on file: tell them yourself. (LinkOnline needs an email, so it is blocked.)'}
              </div>
              {offering && offering.identifier_kind === 'council_id' && offering.hasc_name && (
                <div style={{ fontSize: 12, marginTop: 4, color: '#374151' }}>HASC knows this ID as <strong>{offering.hasc_name}</strong>.</div>
              )}
              {isLinkOnline(location) && <div style={{ fontSize: 12, marginTop: 4, color: '#374151' }}>{LINKONLINE_RULE}</div>}
              <div style={{ ...moneyLine, marginTop: 8 }}>💲 This creates a billable HASC registration.</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                <button type="button" style={{ ...styles.btn, fontSize: 12, padding: '6px 14px', opacity: busy || missing || blockers.length || !course ? 0.5 : 1 }}
                  disabled={busy || !!missing || blockers.length > 0 || !course} onClick={submit}>
                  {busy ? 'Registering…' : 'Confirm: register with HASC'}
                </button>
                <button type="button" style={{ ...styles.btnSecondary, fontSize: 12, padding: '6px 14px' }} disabled={busy} onClick={onClose}>Cancel</button>
                {missing && !blockers.length && course && <span style={{ fontSize: 12, color: colors.textMuted }}>{missing}</span>}
              </div>
            </div>
          ))}

          <div style={{ marginTop: 10 }}>
            <div style={{ ...fieldLabel, marginBottom: 4 }}>HASC registrations for this class</div>
            <HascRegistrationList rows={opts.existing || []} canWrite={!!opts.can_register} showEmployee={false}
              onChanged={(row) => {
                // A cancelled / closed registration re-opens the form (the
                // server decides again whether this class is registrable).
                if (row && !row.is_open) { setResult(null); setSubmitErr(''); setSubmitMaybe(false); }
                setTick(t => t + 1);
                // A status change moves the Site Ready data and the crew plan
                // (an open row blocks the class; a passed one holds it).
                const before = row ? (opts.existing || []).find(x => x.id === row.id) : null;
                if (onChanged && row && (!before || before.status !== row.status || before.is_open !== row.is_open)) onChanged();
              }} />
          </div>
        </>
      )}
    </div>
  );
}

// The crew-wide review ("Register missing classes (N)"): GET
// /registration/job-plan builds the rows from the same evaluation the Site
// Ready chips use; the operator reviews, unticks what he knows is wrong, and
// confirms ONE batch, posted in sequential chunks of at most bulk_max that
// never split one person's classes (one email per person per call).
// refreshKey: anything that changes when the Site Ready data reloads.
export function HascCrewRegister({ projectId, refreshKey, onDone }) {
  const status = useHascStatus(!!projectId);
  const visible = hascVisible(status);
  const [plan, setPlan] = useState(null);
  const [planErr, setPlanErr] = useState('');
  const [planLoading, setPlanLoading] = useState(false);
  const [account, setAccount] = useState(''); // '' = the server's default account
  const [open, setOpen] = useState(false);
  const [rowState, setRowState] = useState({}); // key -> { tick, code, location, date }
  const [batchDate, setBatchDate] = useState('');
  const [po, setPo] = useState('');
  const [pfi, setPfi] = useState('Stop');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [outcomes, setOutcomes] = useState([]); // every batch run while mounted, newest first
  const seq = useRef(0);
  const runSeq = useRef(0);
  const rowsFor = useRef('');
  const batchFor = useRef('');
  // A batch outlives a job switch in Site Access (this panel is keyed on the
  // job): after unmount it sends no further chunks, sets no state and never
  // calls the onDone it captured (that reload is for the OLD job).
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  // Set synchronously on Confirm: `busy` only disables the button after a
  // re-render, so two clicks in the same instant would both send the batch.
  const inFlight = useRef(false);

  // The server's default location, except LinkOnline it could not verify
  // (every course list failed: linkonline_offered null). Then nothing.
  const rowDefaults = (r) => ({ tick: !!r.can_go, code: r.course_code, date: '',
    location: r.default_location && !(isLinkOnline(r.default_location) && r.linkonline_offered !== true) ? r.default_location : '' });

  const loadPlan = () => {
    if (!projectId) return;
    const my = ++seq.current;
    setPlanLoading(true); setPlanErr('');
    api.hascJobPlan(projectId, account || null)
      .then(p => {
        if (my !== seq.current) return;
        setPlan(p);
        // Choices survive a refresh of the same job + account; a new job or
        // account starts from the server's defaults.
        const rk = `${projectId}|${p.account || ''}`;
        const keep = rk === rowsFor.current;
        rowsFor.current = rk;
        setRowState(prev => {
          const next = {};
          for (const r of p.rows || []) next[r.key] = keep && prev[r.key] ? prev[r.key] : rowDefaults(r);
          return next;
        });
        if (batchFor.current !== String(projectId)) {
          batchFor.current = String(projectId);
          setBatchDate(p.today || ''); setPo(p.default_po || ''); setPfi(p.default_pfi || 'Stop');
        }
      })
      .catch(e => { if (my === seq.current) { setPlan(null); setPlanErr(e.message); } })
      .finally(() => { if (my === seq.current) setPlanLoading(false); });
  };
  useEffect(() => { if (visible) loadPlan(); }, [visible, projectId, account, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;

  const rows = plan ? plan.rows || [] : [];
  const setRow = (key, patch) => setRowState(s => ({ ...s, [key]: { ...s[key], ...patch } }));
  const optionFor = (r, st) => (r.course_options || []).find(o => o.course_code === st.code)
    || { course_code: r.course_code, course_name: r.course_name, linkonline_offered: r.linkonline_offered, locations: r.locations || [] };
  const ticked = rows.filter(r => r.can_go && rowState[r.key] && rowState[r.key].tick);
  const needLoc = ticked.filter(r => !rowState[r.key].location).length;
  const bulkMax = (plan && plan.bulk_max) || 60;
  const n = plan ? plan.registrable_count : null;
  const batchCount = ticked.length > bulkMax ? chunkByEmployee(ticked, bulkMax, r => r.employee_id).length : 1;

  const submit = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try { await submitBatch(); } finally { inFlight.current = false; }
  };
  const submitBatch = async () => {
    const list = ticked.map(r => {
      const st = rowState[r.key];
      const opt = optionFor(r, st);
      return { row: r, course_name: opt.course_name, item: { employee_id: r.employee_id, course_code: st.code, location: st.location, desired_date: st.date || batchDate } };
    });
    if (!list.length) return;
    setBusy(true);
    const chunks = chunkByEmployee(list, bulkMax, c => c.item.employee_id);
    const results = [];
    const emails = [];
    let stopped = '';
    let sent = 0;
    for (let ci = 0; ci < chunks.length; ci++) {
      if (!mounted.current) break;
      const chunk = chunks[ci];
      setProgress(`Registering ${sent + 1}-${sent + chunk.length} of ${list.length}…`);
      try {
        const d = await api.hascRegisterBulk({
          project_id: projectId, account: plan.account, po_number: po.trim(), pfi, desired_date: batchDate,
          items: chunk.map(c => c.item),
        });
        for (const res of d.results || []) {
          const c = chunk[res.index] || {};
          results.push({ ...res, name: c.row ? c.row.name : '', course_code: res.course_code || (c.item && c.item.course_code), course_name: c.course_name,
            location: c.item ? c.item.location : '', desired_date: c.item ? c.item.desired_date : '' });
        }
        (d.emails || []).forEach(e => emails.push({ ...e, batch: ci }));
      } catch (e) {
        stopped = `Batch ${ci + 1} failed: ${e.message}`;
        chunk.forEach(c => results.push({ ok: false, unknown: true, name: c.row.name, course_code: c.item.course_code, course_name: c.course_name,
          location: c.item.location, desired_date: c.item.desired_date,
          error: 'Unknown: the request failed. Check the registrations list before trying this one again.' }));
        chunks.slice(ci + 1).flat().forEach(c => results.push({ ok: false, name: c.row.name, course_code: c.item.course_code, course_name: c.course_name,
          location: c.item.location, desired_date: c.item.desired_date, error: 'Not sent: stopped after the failed batch.' }));
        break;
      }
      sent += chunk.length;
    }
    if (!mounted.current) return;
    const runId = ++runSeq.current;
    setOutcomes(o => [{ id: runId, results, emails, stopped }, ...o]);
    // Every submitted row comes back UNTICKED: the plan still on screen until
    // the reload lands is stale, and a second Confirm must be a deliberate
    // re-tick (a row HASC refused stays in the plan and could be re-bought).
    setRowState(s => {
      const next = { ...s };
      for (const c of list) if (next[c.row.key]) next[c.row.key] = { ...next[c.row.key], tick: false };
      return next;
    });
    setBusy(false); setProgress('');
    // Refresh the plan here AND the Site Ready data through the parent (its
    // reload changes refreshKey, which reloads the plan again; a parent whose
    // reload is a no-op can no longer leave the old plan up).
    loadPlan();
    if (onDone) onDone();
  };

  return (
    <div style={{ marginTop: 10 }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ ...styles.btnSecondary, fontSize: 12, padding: '5px 12px', color: '#1E40AF', borderColor: '#93C5FD', background: '#EFF6FF' }}
        title={planErr ? `Could not build the list: ${planErr}` : "Every crew member's failing or expiring class that a HASC location offers, in one review table. Nothing is booked until you confirm."}>
        🎓 Register missing classes ({plan ? n : planLoading ? '…' : '?'}){hascPilotLabel(status)}
      </button>

      {open && (
        <div style={{ marginTop: 8, padding: '10px 12px', border: '1px solid #93C5FD', borderRadius: 8, background: '#F8FAFF' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: colors.charcoal }}>
              🎓 Register missing classes with HASC{hascPilotLabel(status)}
              <div style={{ fontSize: 12, fontWeight: 400, color: colors.textMuted }}>
                {plan && plan.project ? `${plan.project.site || 'Job'}${plan.project.job_number ? ` · ${plan.project.job_number}` : ''}` : ''}
                {plan && plan.customer && plan.customer.name ? ` · vs ${plan.customer.name}` : ''}
                {' · '}one row per person per class that is failing or expiring before the job's horizon. Untick anyone you know already holds it (a class from a council the CRM does not pull is invisible here).
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" style={btnSm} disabled={planLoading || busy} onClick={loadPlan}>{planLoading ? 'Loading…' : 'Reload'}</button>
              <button type="button" style={btnSm} disabled={busy} onClick={() => setOpen(false)}>Close</button>
            </div>
          </div>

          {/* The account select stays up when a plan load fails (one account's
              HASC location list can fail while the other works). */}
          {(status.accounts || []).length >= 2 && (
            <div style={{ marginTop: 8 }}>
              <div style={fieldLabel}>Council account</div>
              <select value={account || (plan && plan.account) || status.default_account || ''} disabled={busy}
                onChange={e => setAccount(e.target.value)} style={{ ...inputSm, width: 130 }}
                title="A class registered under one account shows up only in that account's training feed.">
                {status.accounts.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          )}
          {planErr && <div style={{ ...errBox, marginTop: 8 }}>{planErr}</div>}
          {!plan && planLoading && <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 8 }}>Building the list (checks HASC locations and courses, $0)…</div>}

          {plan && (
            <>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 10 }}>
                <div>
                  <div style={fieldLabel}>Requested date (all rows)</div>
                  <input type="date" value={batchDate} min={plan.today} max="2100-12-31" onChange={e => setBatchDate(e.target.value)} style={{ ...inputSm, width: 150 }} />
                </div>
                <div>
                  <div style={fieldLabel}>PO number</div>
                  <input value={po} onChange={e => setPo(e.target.value)} maxLength={120} placeholder="PO (required)" style={{ ...inputSm, width: 160 }}
                    title="Prints on HASC's bill. Defaults to the job number." />
                </div>
                <div>
                  <div style={fieldLabel}>Pfi</div>
                  <select value={pfi} onChange={e => setPfi(e.target.value)} style={{ ...inputSm, width: 110 }} title={PFI_TITLE}>
                    {(plan.pfi_values || ['Stop', 'Retry', 'Continue']).map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>
                The date is {REQUESTED_NOTE}. A row's own date overrides it. {LINKONLINE_RULE}
              </div>

              {rows.length === 0 ? (
                <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 10 }}>
                  {plan.account ? 'Nothing to register: no crew member has a failing or expiring class that a HASC location offers.' : 'No HASC account is configured.'}
                </div>
              ) : (
                <div style={{ overflowX: 'auto', marginTop: 10 }}>
                  <table style={{ ...styles.table, fontSize: 12 }}>
                    <thead><tr>
                      <th style={styles.th}>
                        <input type="checkbox" aria-label="Tick every row that can go" disabled={busy}
                          checked={rows.some(r => r.can_go) && rows.filter(r => r.can_go).every(r => rowState[r.key] && rowState[r.key].tick)}
                          onChange={e => setRowState(s => {
                            const next = { ...s };
                            for (const r of rows) if (r.can_go && next[r.key]) next[r.key] = { ...next[r.key], tick: e.target.checked };
                            return next;
                          })} />
                      </th>
                      <th style={styles.th}>Employee</th>
                      <th style={styles.th}>Class</th>
                      <th style={styles.th}>Current record</th>
                      <th style={styles.th}>First day onsite</th>
                      <th style={styles.th}>Email</th>
                      <th style={styles.th}>Location</th>
                      <th style={styles.th}>Date</th>
                      <th style={styles.th}>Note</th>
                    </tr></thead>
                    <tbody>
                      {rows.map(r => {
                        const st = rowState[r.key] || rowDefaults(r);
                        const opt = optionFor(r, st);
                        const locs = opt.locations || [];
                        const unv = new Set(opt.course_code === r.course_code ? r.unverified_locations || [] : []);
                        const allUnverified = opt.course_code === r.course_code && r.locations_unverified;
                        const eff = st.date || batchDate;
                        const late = !!(r.first_day_onsite && eff && eff >= r.first_day_onsite);
                        return (
                          <tr key={r.key} style={r.can_go ? {} : { background: '#F9FAFB', color: '#6B7280' }}>
                            <td style={styles.td}>
                              <input type="checkbox" checked={!!(r.can_go && st.tick)} disabled={!r.can_go || busy}
                                aria-label={`Register ${r.name} for ${st.code}`}
                                onChange={e => setRow(r.key, { tick: e.target.checked })} />
                            </td>
                            <td style={styles.td}>
                              <strong>{r.name}</strong>
                              {r.craft ? <div style={{ fontSize: 11, color: colors.textMuted }}>{r.craft}</div> : null}
                            </td>
                            <td style={{ ...styles.td, minWidth: 160 }}>
                              {(r.course_options || []).length > 1 ? (
                                <select value={st.code} disabled={busy} style={inputSm}
                                  onChange={e => {
                                    const o = (r.course_options || []).find(x => x.course_code === e.target.value);
                                    setRow(r.key, { code: e.target.value, location: linkOnlineDefault(o, r.has_email) });
                                  }}>
                                  {r.course_options.map(o => <option key={o.course_code} value={o.course_code}>{o.course_code}{o.course_name ? ` · ${o.course_name}` : ''}</option>)}
                                </select>
                              ) : (
                                <><strong>{opt.course_code}</strong>{opt.course_name ? <div style={{ fontSize: 11, color: colors.textMuted }}>{opt.course_name}</div> : null}</>
                              )}
                              <div style={{ marginTop: 2 }}>
                                <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 8, padding: '1px 6px',
                                  background: r.check_status === 'warn' ? '#FEF3C7' : '#FEE2E2', color: r.check_status === 'warn' ? '#92400E' : '#991B1B' }}>
                                  {r.check_status === 'warn' ? 'Expiring' : 'Missing'}
                                </span>
                              </div>
                            </td>
                            <td style={styles.td}>{r.current_record || 'none on file'}</td>
                            <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{fmtDate(r.first_day_onsite) || '—'}</td>
                            <td style={styles.td}>{r.has_email ? '✓' : <span style={{ color: '#B45309' }}>none</span>}</td>
                            <td style={{ ...styles.td, minWidth: 150 }}>
                              <select value={st.location} disabled={!r.can_go || busy} style={inputSm}
                                onChange={e => setRow(r.key, { location: e.target.value })}>
                                <option value="">Pick…</option>
                                {locs.map(l => {
                                  const online = isLinkOnline(l);
                                  return (
                                    <option key={l} value={l} disabled={online && !r.has_email}>
                                      {l}{online && !r.has_email ? ' (needs email)' : ''}{allUnverified || unv.has(l) ? ' (unverified)' : ''}
                                    </option>
                                  );
                                })}
                              </select>
                            </td>
                            <td style={{ ...styles.td, minWidth: 140 }}>
                              <input type="date" value={st.date} min={plan.today} max="2100-12-31" disabled={!r.can_go || busy}
                                title="Blank = the batch date" onChange={e => setRow(r.key, { date: e.target.value })} style={{ ...inputSm, width: 140 }} />
                              {late && r.can_go && <div style={{ fontSize: 10, color: '#92400E', fontWeight: 700, marginTop: 2 }}>⚠ on or after first day onsite</div>}
                            </td>
                            <td style={{ ...styles.td, maxWidth: 240, fontSize: 11 }}>
                              {!r.can_go && r.blocked_reason && <div style={{ color: '#B91C1C', fontWeight: 600 }}>{r.blocked_reason}</div>}
                              {r.can_go && st.tick && !st.location && <div style={{ color: '#92400E', fontWeight: 600 }}>Pick a location.</div>}
                              {r.identifier_kind === 'council_id' && r.hasc_name && <div>HASC knows this ID as {r.hasc_name}.</div>}
                              {isLinkOnline(st.location) && <div style={{ color: colors.textMuted }}>LinkOnline: 5 days to start, 5 to finish.</div>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {rows.length > 0 && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 10, paddingTop: 8, borderTop: `1px solid ${colors.border}` }}>
                  <span style={{ ...moneyLine, fontSize: 13 }}>
                    💲 {ticked.length} billable registration{ticked.length === 1 ? '' : 's'}
                  </span>
                  {needLoc > 0 && <span style={{ fontSize: 12, color: '#92400E' }}>{needLoc} ticked row{needLoc === 1 ? '' : 's'} need{needLoc === 1 ? 's' : ''} a location.</span>}
                  {!po.trim() && <span style={{ fontSize: 12, color: '#92400E' }}>PO number required.</span>}
                  {batchCount > 1 && <span style={{ fontSize: 12, color: colors.textMuted }}>Sent in {batchCount} batches of up to {bulkMax}, one after another, never splitting one person's classes.</span>}
                  <span style={{ flex: 1 }} />
                  {progress && <span style={{ fontSize: 12, color: colors.textMuted }}>{progress}</span>}
                  <button type="button" style={{ ...styles.btn, fontSize: 12, padding: '6px 14px', opacity: busy || !ticked.length || needLoc || !batchDate || !po.trim() ? 0.5 : 1 }}
                    disabled={busy || !ticked.length || needLoc > 0 || !batchDate || !po.trim()} onClick={submit}>
                    {busy ? 'Registering…' : `Confirm: register ${ticked.length} with HASC`}
                  </button>
                </div>
              )}

              {/* Every run while this panel is mounted, newest first: a second
                  batch never wipes the first one's HASC numbers or its
                  "tell them yourself" list. */}
              {outcomes.map((outcome, oi) => {
                const registered = outcome.results.filter(r => r.ok).length;
                const maybe = outcome.results.filter(mayBeBooked).length;
                const refused = outcome.results.length - registered - maybe;
                return (
                <div key={outcome.id} style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: colors.charcoal }}>
                    {oi === 0 ? 'Result' : 'Earlier run'}: {registered} registered
                    {maybe > 0 && <span style={{ color: '#92400E' }}> · {maybe} may be booked (check before retrying)</span>}
                    {' '}· {refused} not registered
                  </div>
                  {outcome.stopped && <div style={{ ...errBox, marginTop: 6 }}>{outcome.stopped}</div>}
                  <div style={{ overflowX: 'auto', marginTop: 6 }}>
                    <table style={{ ...styles.table, fontSize: 12 }}>
                      <thead><tr>
                        <th style={styles.th}>Employee</th>
                        <th style={styles.th}>Class</th>
                        <th style={styles.th}>Location</th>
                        <th style={styles.th}>Requested</th>
                        <th style={styles.th}>Result</th>
                      </tr></thead>
                      <tbody>
                        {outcome.results.map((r, i) => (
                          <tr key={i}>
                            <td style={styles.td}>{r.name || '—'}</td>
                            <td style={styles.td}><strong>{r.course_code}</strong>{r.course_name ? ` · ${r.course_name}` : ''}</td>
                            <td style={styles.td}>{r.location || '—'}</td>
                            <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{fmtDate(r.desired_date) || '—'}</td>
                            <td style={{ ...styles.td, maxWidth: 360 }}>
                              {r.ok ? (
                                <span style={{ color: '#065F46', fontWeight: 600 }}>
                                  ✓ Registered{r.registration && r.registration.registration_id ? ` #${r.registration.registration_id}` : ''}
                                </span>
                              ) : mayBeBooked(r) ? (
                                <span style={{ color: '#92400E', fontWeight: 600 }}>⚠ May be booked: {r.error || 'unknown.'}</span>
                              ) : (
                                <span style={{ color: '#B91C1C' }}>✗ {r.error || 'Not registered.'}</span>
                              )}
                              {!r.ok && r.hasc_error && !(r.error || '').includes(r.hasc_error) && <div style={{ color: mayBeBooked(r) ? '#92400E' : '#B91C1C' }}>HASC: {r.hasc_error}</div>}
                              {r.warning && <div style={{ color: '#92400E' }}>{r.warning}</div>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {outcome.emails.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 12 }}>
                      <div style={{ ...fieldLabel, marginBottom: 2 }}>Employee emails (one per person, you are copied)</div>
                      {outcome.emails.map(e => (
                        <div key={`${e.employee_id}|${e.batch}`} style={{ color: e.sent ? '#374151' : '#92400E', fontWeight: e.sent ? 400 : 600 }}>
                          {e.name} ({e.registrations} class{e.registrations === 1 ? '' : 'es'}): {e.sent ? `emailed ${e.to}${e.cc ? ` (cc ${e.cc})` : ''}` : (e.warning || e.error || 'not sent: tell them yourself.')}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                );
              })}

              {(plan.not_registrable || []).length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ ...fieldLabel, marginBottom: 4 }}>Not registrable here ({plan.not_registrable.length})</div>
                  <div style={{ maxHeight: 220, overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: 6, background: '#fff' }}>
                    {plan.not_registrable.map((x, i) => (
                      <div key={i} style={{ fontSize: 12, padding: '4px 8px', borderBottom: `1px solid ${colors.graySoft}` }}>
                        <strong>{x.name || 'Open seat'}</strong>{x.craft ? <span style={{ color: colors.textMuted }}> · {x.craft}</span> : null}
                        {x.label ? <> · {x.label}</> : null}
                        <span style={{ color: colors.textMuted }}> · {x.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Admin -> Backup "HASC Training Sync" card: the pilot switch, the $0
// location / course check, and the 20 newest registrations.
export function HascRegistrationAdmin() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [catalog, setCatalog] = useState(null);
  const [checking, setChecking] = useState(false);
  const [recent, setRecent] = useState(null);
  const [recentErr, setRecentErr] = useState('');

  const loadStatus = () => api.hascRegistrationStatus().then(setStatus).catch(e => setError(e.message));
  const loadRecent = () => api.hascRegistrations()
    .then(r => { setRecent(Array.isArray(r) ? r : []); setRecentErr(''); })
    .catch(e => setRecentErr(e.message));
  useEffect(() => { loadStatus(); loadRecent(); }, []);

  const toggle = async () => {
    if (!status) return;
    setError('');
    try {
      await api.updateSetting('hasc_registration_enabled', status.enabled ? 'false' : 'true');
      await loadStatus();
    } catch (e) { setError(e.message); }
  };
  const check = async () => {
    setChecking(true); setError('');
    try { setCatalog(await api.hascCatalog()); }
    catch (e) { setError(e.message); }
    finally { setChecking(false); }
  };

  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${colors.border}` }}>
      <h3 style={{ ...styles.h2, fontSize: 15, marginBottom: 6 }}>Register with HASC</h3>
      <p style={{ color: colors.textMuted, fontSize: 13, marginTop: 0 }}>
        The Site Ready modal and the job's crew view can register people for Safety Council classes. Every
        registration is billable to SSS. While registration is off (the pilot), only admin and leadership can
        register; turning it on lets ops and supervisors register too. Status checks run nightly at 8 PM Central.
      </p>
      {error && <div style={styles.errorBox}>{error}</div>}
      {status && (
        <table style={{ ...styles.table, marginBottom: 12 }}>
          <tbody>
            <tr><td><strong>Registration</strong></td>
              <td>
                {status.enabled ? '✓ on for ops and supervisors' : '✗ off (pilot: admin and leadership only)'}{' '}
                <button type="button" onClick={toggle} style={{ ...styles.btnSecondary, padding: '2px 10px', fontSize: 12, marginLeft: 8 }}>
                  Turn {status.enabled ? 'off' : 'on'}
                </button>
              </td>
            </tr>
            <tr><td><strong>Accounts with a key</strong></td>
              <td>{(status.accounts || []).join('  ·  ') || '✗ none configured'}</td>
            </tr>
          </tbody>
        </table>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" onClick={check} disabled={checking || !(status && status.configured)} style={styles.btnSecondary}
          title="Reads HASC's location list and every location's course list. Free: nothing is booked.">
          {checking ? 'Checking HASC…' : 'Check locations & courses ($0)'}
        </button>
        <button type="button" onClick={loadRecent} style={styles.btnSecondary}>Refresh list</button>
      </div>
      {catalog && (
        <div style={{ marginTop: 10 }}>
          {(catalog.accounts || []).length === 0 && <div style={{ fontSize: 12, color: colors.textMuted }}>No account is configured.</div>}
          {(catalog.accounts || []).map(a => (
            <div key={a.account} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>
                {a.account}{a.ok ? `: ${a.locations.length} location${a.locations.length === 1 ? '' : 's'}` : ''}
              </div>
              {!a.ok && <div style={{ ...styles.errorBox, marginBottom: 0 }}>{a.error}</div>}
              {a.ok && (
                <div style={{ fontSize: 12, color: '#374151' }}>
                  {a.locations.map(l => (
                    <span key={l.location} style={{ display: 'inline-block', marginRight: 12 }}>
                      {l.location}: {l.error ? <span style={{ color: '#B91C1C' }}>{l.error}</span> : `${l.course_count} course${l.course_count === 1 ? '' : 's'}`}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 12 }}>
        <div style={{ ...fieldLabel, marginBottom: 4 }}>20 most recent registrations</div>
        {recentErr && <div style={styles.errorBox}>{recentErr}</div>}
        {recent === null && !recentErr && <div style={{ fontSize: 12, color: colors.textMuted }}>Loading…</div>}
        {recent && <HascRegistrationList rows={recent} canWrite={!!(status && status.can_register)} onChanged={loadRecent} />}
      </div>
    </div>
  );
}
